import { Injectable } from '@angular/core';
import { CustomerdatabaseService } from './customerdatabase.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { JobOfferStandardQuestions, JobOfferKeywords, QUESTIONS_LANGUAGE_MAP } from '../core/helper/JobOfferStandardQuestions';
import { TSMap } from 'typescript-map';
import { TranslationService } from './translation.service';
import { Constants } from '../core/helper/Constants';

/**
 * This service does the following:
 * 1. fetch all job offers from Firestore and store them in an array.
 * 2. filter job offers based on certain conditions.
 * 3. delete job offers from the array for display on kendo-grid.
 * 4. return the array of job offers.
 * 5. return the array of all, draft, test, restored job offers.
 * 6. add or update job offer for grid.
 * 7. remove job offer from deleted job offers.
 */
@Injectable({
  providedIn: 'root'
})
export class JobOfferService {

  allJobOffers: any[] = [];
  jobOffers: any = [];
  public isJobOfferDataFetchedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isJobOfferDataFetched$: Observable<boolean> = this.isJobOfferDataFetchedSubject.asObservable();

  public isJobOfferDataUpdatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isJobOfferDataUpdated$: Observable<boolean> = this.isJobOfferDataUpdatedSubject.asObservable();

  public isJobOfferDataDeletedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isJobOfferDataDeleted$: Observable<boolean> = this.isJobOfferDataDeletedSubject.asObservable();
  deletedJobOffers: any[] = [];
  testModeJobOffers: any[] = [];
  draftJobOffers: any[] = [];
  serverurl: string;
  templatedJobOffers: any[];
  listOfKeywords = JobOfferKeywords.listKeysDeutsch;

  /**
   * Lightweight in-memory cache of messages per job offer.
   * This allows the job-offers list view to access messages needed
   * for video status checks without loading full documents from Firestore.
   */
  private jobOfferMessagesCache: { [id: string]: any[] } = {};

  //  Edit by Yuliang Huang, 02.10.2024
  //  the value isJobOfferDataUpdated will be used, to check if the job offer data is updated or not, especially for the translation of messages in germany.
  isJobOfferDataUpdated: boolean = false;

  constructor(private customerDB: CustomerdatabaseService, private translationService: TranslationService) {
    // this.isJobOfferDataUpdatedSubject.subscribe(
    // {
    //   next: (value) => {
    //     this.isJobOfferDataUpdated = value;
    //   },
    //   error: (err) => {
    //     console.log("Error in isJobOfferDataUpdatedSubject subscription", err);
    //   },
    //   complete: () => {
    //     console.log("isJobOfferDataUpdatedSubject subscription complete");
    //   }
    // }
    // )
  }

  /**
   * Cache messages for a given job offer id and also attach them
   * to any already-loaded job offer objects in memory.
   * This is called from job-offer creation / update flows.
   */
  cacheJobOfferMessages(id: string, messages: any[]): void {
    if (!id || !Array.isArray(messages)) {
      return;
    }
    this.jobOfferMessagesCache[id] = messages;

    const allItem = this.allJobOffers.find((o) => o && o.id === id);
    if (allItem) {
      allItem.messages = messages;
    }
    const activeItem = (Array.isArray(this.jobOffers) ? this.jobOffers : []).find((o) => o && o.id === id);
    if (activeItem) {
      activeItem.messages = messages;
    }
  }

  /**
   * Returns cached messages for a given job offer id, if available.
   */
  getCachedMessages(id: string): any[] | undefined {
    if (!id) {
      return undefined;
    }
    return this.jobOfferMessagesCache[id];
  }

  /**
   * Internal helper to apply cached messages to a job offer object.
   */
  private applyCachedMessages(jobOffer: any): void {
    if (!jobOffer || jobOffer.messages) {
      return;
    }
    const cached = this.getCachedMessages(jobOffer.id);
    if (cached && Array.isArray(cached)) {
      jobOffer.messages = cached;
    }
  }

  /**
   * This method is used to fetch all deleted job offers from Firestore
   * and setting the observable to true after completing fetch.
   */
  async fetchDeletedJobOffers() {
    this.deletedJobOffers = [];
    await this.customerDB.getDeletedJobOffers().get().then((snap) => {
      snap.forEach((doc) => {
        let data = doc.data();
        let id = doc.id;
        data.url = this.getJobOfferUrl(id);
        data.location = data.city;
        data.numberOfApplications = data.applicants ? data.applicants.length : 0;
        this.deletedJobOffers.push({ id, ...data });
      })
    });
    console.log(">>>>>>>>>>>>>> Deleted Job Offers: ", this.deletedJobOffers);
    this.isJobOfferDataDeletedSubject.next(true);
    return this.deletedJobOffers;
  }

  /**
   * This function is used to find all draft status job offers.
   * @returns 
   */
  getDraftJobOffers(): any[] {
    this.draftJobOffers = [];
    console.log("Fetching draft job offers...");
    let offers: any[] = this.returnAllJobOffers().filter((element) => element.status === 'draft');
    offers.forEach((element: any) => {
      let id = element.id;
      let publicType: boolean;
      element.url = this.getJobOfferUrl(id);
      element.location = element.city;
      element.numberOfApplications = element.applicants ? element.applicants.length : 0;
      element.isNotGenerated = element.isNotGenerated ? element.isNotGenerated : false;
      element.progressValue = this.getProgressValue(element);
      this.applyCachedMessages(element);
      // if (element.public === true) {
      //   publicType = true;
      // } else if (element.public === "") {
      //   publicType = true;
      // } else {
      //   publicType = false;
      // }
      console.log("Draft Job Offer: ", element);
      this.draftJobOffers.push({ id, ...element });
    });
    console.log(">>>>>>>>>>>>>> Draft Job Offers: ", this.draftJobOffers);
    return this.draftJobOffers;
  }

  /**
   * Normalizes job offers by converting updated timestamps to Date objects
   * @param jobOffers Array of job offers to normalize
   * @returns Array of normalized job offers
   */
  normalizeJobOffers(jobOffers: any[]): any[] {
    if (!jobOffers || !Array.isArray(jobOffers)) {
      return jobOffers || [];
    }
    return jobOffers.map(jobOffer => {
      if (jobOffer.updated) {
        jobOffer.updated = this.normalizeTimestamp(jobOffer.updated) || jobOffer.updated;
      }
      return jobOffer;
    });
  }

  /**
   * Normalizes a timestamp to a Date object regardless of input format
   * @param timestamp Firestore Timestamp, Date, number (ms), or string
   * @returns Date object or null if invalid
   */
  private normalizeTimestamp(timestamp: any): Date | null {
    if (!timestamp) return null;

    if (timestamp instanceof Date) {
      return timestamp;
    }

    if (timestamp && typeof timestamp.toDate === 'function') {
      // Firestore Timestamp
      return timestamp.toDate();
    }

    if (typeof timestamp === 'number') {
      // Milliseconds
      return new Date(timestamp);
    }

    if (typeof timestamp === 'string') {
      // ISO string
      return new Date(timestamp);
    }

    // Fallback: try to convert
    try {
      return new Date(timestamp);
    } catch {
      return null;
    }
  }

  /**
  * This is a function to calculate if the updated is more than 10 minutes or less.
  * @param dataItem JobOffer
  * @returns the progress value for the kendo-progressbar
  */
  getProgressValue(dataItem) {
    if (dataItem.updated == undefined) {
      //also update broken job offer in db that does not have updated value. 
      // Moved to draft if it does not have any status.
      this.customerDB.updateJobOffer(dataItem.id, { status: dataItem.status || 'draft', updated: new Date() });
      return 0;
    }

    // updated should now always be a Date object (normalized at fetch time)
    const updatedDate = dataItem.updated instanceof Date
      ? dataItem.updated
      : this.normalizeTimestamp(dataItem.updated);

    if (!updatedDate) {
      return 0;
    }

    let value = Math.floor((Date.now() - updatedDate.getTime()) / 6000);
    return value > 100 ? 100 : value;
  }

  /**
  * This is a function to check the progress of the Job-Offer (status der Bereitstellung)
  * @param dataItem JobOffer
  * @returns true if the video is already generated
  */
  checkProgress(dataItem) {
    //case for video is made before 10 minute mark
    if (dataItem.notGeneratedCount == 0 || dataItem.notGeneratedCount == undefined) {
      if (dataItem.progressValue < 100) {
        dataItem.progressValue++;
      } else {
        //this is for undefined progressValue
        dataItem.progressValue = 100;
      }
      dataItem.isNotGenerated = false;
      return;
    }
    //get value for the progressbar
    dataItem.progressValue = this.getProgressValue(dataItem);
    // //case for video is not yet ready after 10 minutes
    if (dataItem.progressValue == 100) {
      if (dataItem.notGeneratedCount != 0 && dataItem.notGeneratedCount != undefined) {
        // dataItem.progressValue = 99;
        dataItem.isNotGenerated = true;
      } else {
        dataItem.isNotGenerated = false;
      }
    }
  }

  /**
   * @returns deleted job offers
   */
  getDeletedJobOffers(): any[] {
    return this.deletedJobOffers;
  }

  /**
   * @returns test mode job offers
   */
  getTestModeJobOffers(): any[] {
    this.testModeJobOffers = [];
    console.log("Fetching test mode job offers...");
    let offers: any[] = this.returnAllJobOffers().filter((element) => element.status === 'testmode');
    offers.forEach((element: any) => {
      //  snap.forEach((doc) => {
      var data = element;
      var id = data.id;

      data.location = data.city;
      data.numberOfApplications = data.applicants ? data.applicants.length : 0;
      data.url = this.getJobOfferUrl(id);
      data.isNotGenerated = data.isNotGenerated ? data.isNotGenerated : false;
      data.progressValue = this.getProgressValue(element);
      console.log(data);
      this.testModeJobOffers.push({ id, ...data });
    });

    // if duplicates are present in job offers then keep 1 remove rest
    let jobOfferIds = [];
    let newArray = [];
    //let duplicateJobOffers = [];
    this.testModeJobOffers.forEach((element: any) => {
      jobOfferIds.push(element.id);
    });
    for (let i = 0; i < this.testModeJobOffers.length; i++) {
      if (!newArray.some((item) => item.id === this.testModeJobOffers[i].id)) {
        console.log("Duplicates found in test mode job offers: thus removing duplicates: ");
        newArray.push(this.testModeJobOffers[i]);
      }
    }
    this.testModeJobOffers = [];
    this.testModeJobOffers = newArray;
    console.log(">>>>>>>>>>>>>> Test Mode Job Offers: ", this.testModeJobOffers);
    return this.testModeJobOffers;
  }

  /**
   * @returns job offers where status is public or aktiv or empty ('')
   */
  returnJobOffers() {
    //check if any job offers are in draft mode but not in job offers, then add them to job offers
    //  this.checkIfJobOfferExistsInDraftOrTestModeButNotInJobOffers();
    //check if any job offers are present with public as 
    this.jobOffers = [];
    this.allJobOffers.forEach(element => {
      if (element.status === 'public' || element.status === 'aktiv' || element.status === '') {  //for now we have 3 status for public, but will change it to public someday
        if (element.status === 'public') {
          // console.log('job with public found', element);
        }
        element.isNotGenerated = element.isNotGenerated ? element.isNotGenerated : false;
        element.progressValue = this.getProgressValue(element);
        this.applyCachedMessages(element);
        this.jobOffers.push(element);
      }
    });
    return this.jobOffers;
  }
  /**
   * @returns all job offers
   */
  returnAllJobOffers() {
    this.allJobOffers.forEach(element => {
      element.isNotGenerated = element.isNotGenerated ? element.isNotGenerated : false;
      element.progressValue = this.getProgressValue(element);
      this.applyCachedMessages(element);
    });
    return this.allJobOffers;
  }

  async fetchAllJobOffers(): Promise<any[]> {
    try {
      const snap = await this.customerDB.getJobOffers().get();
      this.allJobOffers = [];
      snap.forEach((doc) => {
        const data = doc.data();
        // Normalize updated timestamp to Date object
        if (data.updated) {
          data.updated = this.normalizeTimestamp(data.updated) || data.updated;
        }
        this.allJobOffers.push({ id: doc.id, ...data });
      });
      this.isJobOfferDataFetchedSubject.next(true);
      return this.allJobOffers;
    } catch (error) {
      console.error('Error fetching job offers:', error);
      return [];
    }
  }

  /**
   * Currently not in use added for future use.
   * @returns templated job offers
   */
  getTemplatedJobOffers(): any[] {
    this.templatedJobOffers = [];
    console.log("Fetching templated job offers...");
    let offers: any[] = this.returnAllJobOffers();
    offers.forEach((element: any) => {
      let data = element;
      let id = data.id;
      if (data.status === "template") {
        this.templatedJobOffers.push({ id, ...data });
      }
    });
    return this.templatedJobOffers;
  }

  /**
   * Function to get job offer by id from all job offers, if not present make call in database to check again.
   * @param id 
   * @returns 
   */
  async getJobOffer(id: string) {
    let jobOffer = this.allJobOffers.find((jobOffer) => jobOffer.id === id);
    if (jobOffer) {
      console.log("Job Offer found in job offers: ", jobOffer);
      return jobOffer;
    } else {
      try {
        const snap = await this.customerDB.getJobOffersDetail(id).get();
        const data = snap.data();

        if (!data) {
          console.error("No data found for the provided ID" + id);
          // throw new Error('No data found for the provided ID.');
        } else {
          data.id = snap.id;
          data.location = data.city;
          data.url = this.getJobOfferUrl(data.id);
          data.numberOfApplications = data.applicants ? data.applicants.length : 0;
          jobOffer = data;
          return jobOffer;
        }
      } catch (error) {
        // console.error("Error fetching job offer details:", error);
        // throw error; // Re-throw the error to be handled by the caller
        return undefined;
      }
    }
  }

  async updateJobOfferMessages(id: string, messages: any[]): Promise<void> {
    try {
      await this.customerDB.getJobOffersDetail(id).update({ messages });
    } catch (error) {
      console.error('Error updating job offer messages:', error);
    }
  }

  private getJobOfferUrl(id) {
    let serverurl = this.customerDB.getHostingURL();
    return serverurl + "/" + "interaction" + "/" + id;
  }

  /**
   * Function to generate messages/ questions map in different languages.
   * @returns 
   */
  // generateBasicMessagesMapInDifferentLanguages() {
  //   let listQuestionsDeutsch = JobOfferStandardQuestions.listQuestionsDeutsch;
  //   let listQuestionsEnglish = JobOfferStandardQuestions.listQuestionsEnglish;
  //   let listQuestionsFranzösisch = JobOfferStandardQuestions.listQuestionsFranzösisch;
  //   let listQuestionsItalienisch = JobOfferStandardQuestions.listQuestionsItalienisch;
  //   let questionsMap: TSMap<string, any> = new TSMap<string, any>([]);
  //   for (let i = 0; i < this.listOfKeywords.length; i++) {
  //     if (listQuestionsDeutsch[i] && listQuestionsEnglish[i] && listQuestionsFranzösisch[i] && listQuestionsItalienisch[i]) {
  //       questionsMap.set(this.listOfKeywords[i], { deutsch: listQuestionsDeutsch[i], englisch: listQuestionsEnglish[i], französisch: listQuestionsFranzösisch[i], italienisch: listQuestionsItalienisch[i] });
  //     }
  //   }
  //   return questionsMap;
  // }

  /**
   * Function to generate basic keywords map in different languages.
   * @returns
   * */
  // generateKeywordsMapInDifferentLanguages() {
  //   let listKeysDeutsch = JobOfferKeywords.listKeysDeutsch;
  //   let listKeysEnglish = JobOfferKeywords.listKeysEnglish;
  //   let listKeysfranzösisch = JobOfferKeywords.listKeysfranzösisch;
  //   let listKeysItalienisch = JobOfferKeywords.listKeysItalienisch;
  //   let keysMap: TSMap<string, QUESTIONS_LANGUAGE_MAP> = new TSMap<string, QUESTIONS_LANGUAGE_MAP>([]);
  //   for (let i = 0; i < this.listOfKeywords.length; i++) {
  //     keysMap.set(this.listOfKeywords[i], { deutsch: listKeysDeutsch[i], englisch: listKeysEnglish[i], französisch: listKeysfranzösisch[i], italienisch: listKeysItalienisch[i] });
  //   }
  //   return keysMap;
  // }

  /**
   * Function to generate messages map in different languages for newly added message. This also overwrites the generic standardMessage map if the question is edited. 
   * @param translatedValues languageMap that has been translated
   * @returns newly created messageMap with additional standard messages that is missing. 
   */
  // generateBasicMessagesMapInDifferentLanguagesForNewMessage(translatedValues: TSMap<string, any>) {
  //   let basicMap = this.generateBasicMessagesMapInDifferentLanguages();
  //   let messageMap: TSMap<string, any> = new TSMap<string, any>([]);
  //   messageMap = basicMap;
  //   translatedValues.forEach((value, key) => {
  //     messageMap.set(key, value);
  //   });
  //   return messageMap;
  // }

  // /**
  //  * Function to check default language and set flags.
  //  * @param defaultJobOfferLanguage 
  //  * @returns 
  //  */
  // checkDefaultLanguageAndSetFlags(defaultJobOfferLanguage: string) {
  //   let supportedLanguages = JobOfferKeywords.supportedLanguages;
  //   if (defaultJobOfferLanguage == supportedLanguages[0]) {
  //     return { isDeutsch: true, isEnglisch: false, isFrench: false, isItalienisch: false };
  //   } else if (defaultJobOfferLanguage == supportedLanguages[1]) {
  //     return { isDeutsch: false, isEnglisch: true, isFrench: false, isItalienisch: false };
  //   } else if (defaultJobOfferLanguage == supportedLanguages[2]) {
  //     return { isDeutsch: false, isEnglisch: false, isFrench: true, isItalienisch: false };
  //   } else if (defaultJobOfferLanguage == supportedLanguages[3]) {
  //     return { isDeutsch: false, isEnglisch: false, isFrench: false, isItalienisch: true };
  //   }
  // }

  /**
   * Function to remove duplicates from an array of objects.
   * @param keyWordToBeTranslated 
   * @returns 
   */
  removeDuplicates(keyWordToBeTranslated: any[]): any[] {
    // this keywordToBeTranslated is an array of objects of structure {key: "", value: ""} find and keep only unique objects
    let unique = [];
    let uniqueKeys = [];
    keyWordToBeTranslated.forEach(element => {
      if (!uniqueKeys.includes(element.key)) {
        uniqueKeys.push(element.key);
        unique.push(element);
      }
    });
    return unique;
  }

  /**
   * Function to find translations for a given item.
   * @param item 
   * @param data 
   * @returns 
   */
  async findTranslations(item, data) {
    let supportedLanguages = Constants.DEUTSCH;
    const translationResult = await this.translationService.deeplTranslateWithSourceLang([data], supportedLanguages, Constants.DEUTSCH);
    item.textDeutsch = translationResult[0].translations.translations[0].text;
    // await this.translationService.deeplTranslateWithSourceLang([data], supportedLanguages[1], currentViewJobOfferMessagesLanguage).then((res) => {
    //   item.languageMap[supportedLanguages[1]] = res[0].translations.translations[0].text;
    // });
    // await this.translationService.deeplTranslateWithSourceLang([data], supportedLanguages[2], currentViewJobOfferMessagesLanguage).then((res) => {
    //   item.languageMap[supportedLanguages[2]] = res[0].translations.translations[0].text;
    // });
    // await this.translationService.deeplTranslateWithSourceLang([data], supportedLanguages[3], currentViewJobOfferMessagesLanguage).then((res) => {
    //   item.languageMap[supportedLanguages[3]] = res[0].translations.translations[0].text;
    // });
    return item;
  }

  /**
   * Function to find a key in map and update its value.
   * @param existingMap 
   * @param key 
   * @param value 
   * @returns 
   */
  findAKeyInMapAndUpdateItsValue(existingMap: TSMap<string, any>, key: string, value: any) {
    if (existingMap.has(key)) {
      existingMap.set(key, value);
    }
    return existingMap;
  }

  /**
   * Function to find a key in map and update one particular language value.
   * @param messageMap 
   * @param key 
   * @param text 
   * @param langFromEdit 
   * @returns 
   */
  // findAKeyInMapAndUpdateOneParticularLanguageValue(messageMap: TSMap<string, any>, key: any, text: any, langFromEdit: any): TSMap<string, any> {
  //   if (messageMap.has(key)) {
  //     let val = messageMap.get(key);
  //     val[langFromEdit] = text;
  //     messageMap.set(key, val);
  //   }
  //   return messageMap; 
  // }

  getJobOfferTitleById(jobOfferId) {
    let jobTitle = this.allJobOffers.find((jobOffer) => jobOffer.id === jobOfferId).jobTitle || jobOfferId;
    return jobTitle;
  }
}